import { z } from "zod";
import { blockVisibilitySchema } from "@tellescope/visibility-schema";

// Shop product interval - Stripe recurring-price billing interval
export const shopProductIntervalSchema = z.enum(["day", "week", "month", "year"]);
export type ShopProductInterval = z.infer<typeof shopProductIntervalSchema>;

// Shop price variant schema - a single selectable Stripe price for a shop product (e.g. monthly
// vs. quarterly billing of the same medication). Enduser-facing display groups these under one
// product card/cart entry; the enduser picks a variant, not a separate product.
export const shopPriceVariantSchema = z.object({
  // Stripe price id, e.g. "price_..." - the reverse-lookup key at checkout time for this variant
  stripePriceId: z.string(),
  // Smallest-currency-unit price (e.g. cents for usd), snapshotted from the Stripe Price at admin-save time
  amount: z.number().int().min(0),
  // ISO 4217 currency code, lowercase, matching Stripe's own convention (e.g. "usd")
  currency: z.string().regex(/^[a-z]{3}$/, "Must be a lowercase 3-letter ISO 4217 currency code"),
  isSubscription: z.boolean().default(false),
  // Only meaningful when isSubscription is true
  interval: shopProductIntervalSchema.optional(),
  // Only meaningful when isSubscription is true. Distinguishes e.g. quarterly (interval: "month",
  // intervalCount: 3) from monthly (interval: "month", intervalCount: 1) - Stripe has no separate
  // "quarter" interval value, so this is required to tell such variants apart.
  intervalCount: z.number().int().min(1).optional(),
});
export type ShopPriceVariant = z.infer<typeof shopPriceVariantSchema>;

// Shop product entry schema - one product with 1+ selectable price variants, snapshotted by the
// admin editor from the shop-products/shop-prices proxy_read types at save time. This is display
// content, not a live-queried reference — the enduser-facing catalog reads it directly with no
// fetch; only checkout resolves a chosen variant's stripePriceId back to a Tellescope Product via
// reverse lookup (products.getSome, filtered by stripePriceId).
// Each Stripe price still maps to its own distinct Tellescope Product record (a real backend
// constraint - prepare_stripe_checkout has no explicit sort when a Product has multiple active
// prices), but that split is invisible to the enduser: one product renders as one card, with a
// variant selector where there's more than one price - not one card per price.
export const shopProductSchema = z.object({
  // Stripe product id, e.g. "prod_..." - shared by every variant of this product
  stripeProductId: z.string(),
  title: z.string(),
  description: z.string().optional(),
  image: z.string().optional(),
  // Admin-curated grouping label for the catalog's filter chips (e.g. "Hair Loss", "Weight Loss")
  category: z.string().optional(),
  // Admin-curated badge text shown on the product card (e.g. "Popular", "New")
  badge: z.string().optional(),
  // Admin-curated flag - includes this product in the catalog's "recommended" rail
  recommended: z.boolean().default(false),
  // Optional intake-form association, one per product (not per variant) - eligibility doesn't
  // change based on which billing cadence is selected. Absent means no gating is required.
  // v1 ships with no admin UI to set this (FormAssociationPicker removed from the editor) and no
  // product will have a value here - kept unused so the forms fast follow can wire it back up
  // without a schema migration.
  formId: z.string().optional(),
  // At least one price variant is required; the catalog defaults to the first when adding to cart.
  variants: z.array(shopPriceVariantSchema).min(1),
  // Per-product conditional display, evaluated against the current enduser/patient data at render
  // time - same condition system used for whole-block/page visibility. Absent means always visible.
  visibility: blockVisibilitySchema.optional(),
});
export type ShopProduct = z.infer<typeof shopProductSchema>;
